import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Box,
  Divider,
  Paper,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import * as XLSX from 'xlsx';
import api from '../services/api';
import KpiDrillDownModal from '../components/KpiDrillDownModal';
import InfoTooltip from '../components/InfoTooltip';
import { processWarehouseInventory } from '../utils/warehouseInventoryProcessor';

// Logo import
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

// Hardcoded drill-down data for demo
const DEMO_DRILLDOWN_DATA = {
  lphPicking: {
    type: 'time_series',
    data: [
      { date: '2024-09-28', value: 43 },
      { date: '2024-09-30', value: 46 },
      { date: '2024-10-01', value: 37 },
      { date: '2024-10-02', value: 36 },
      { date: '2024-10-03', value: 28 },
      { date: '2024-10-04', value: 43 },
      { date: '2024-10-05', value: 40 },
      { date: '2024-10-06', value: 60 }
    ]
  },
  skuInStockPlatform: {
    type: 'by_platform',
    data: [
      { platform: 'ATL', value: 73.6 },
      { platform: 'AVL', value: 96.4 },
      { platform: 'BNA', value: 89.4 },
      { platform: 'CAE', value: 95.4 },
      { platform: 'GSO', value: 69.3 },
      { platform: 'MEM', value: 79.4 },
      { platform: 'PNS', value: 65.4 },
      { platform: 'RDU', value: 59.0 },
      { platform: 'SAV', value: 67.1 },
      { platform: 'TYS', value: 60.5 }
    ]
  },
  vendorsConvertedSales: {
    type: 'vendor_list',
    data: [
      { vendor: 'Vistar' },
      { vendor: 'Wolf' },
      { vendor: 'Emerge Technologies Inc (Helix)' },
      { vendor: 'JBL US' },
      { vendor: 'Nimble for Good, PBC' },
      { vendor: 'PEAG LLC DBA JLAB AUDIO' },
      { vendor: 'SKULLCANDY, INC.' },
      { vendor: 'Sudio AB, LLC' },
      { vendor: 'Sunshine Computers and Software Inc' },
      { vendor: 'VLC/Tucano US' }
    ]
  },
  pickAccuracy: {
    type: 'time_series_with_volume',
    data: [
      { date: '2024-04-01', value: 89.23, volume: 311251 },
      { date: '2024-05-01', value: 83.26, volume: 714622 },
      { date: '2024-06-01', value: 83.68, volume: 753286 },
      { date: '2024-07-01', value: 86.08, volume: 880612 },
      { date: '2024-08-01', value: 86.28, volume: 857834 },
      { date: '2024-09-01', value: 87.3, volume: 772226 },
      { date: '2024-10-01', value: 86.32, volume: 400236 }
    ]
  },
  onTimeDelivery: {
    type: 'time_series',
    data: [
      { date: '2024-09-07', value: 88 },
      { date: '2024-09-14', value: 79.17 },
      { date: '2024-09-21', value: 100 },
      { date: '2024-09-28', value: 88 },
      { date: '2024-10-05', value: 75 }
    ]
  }
};

// Map destination names from delivery file to platform codes
const DESTINATION_TO_PLATFORM = {
  'RDU - Raleigh Durham': 'RDU',
  'Raleigh Durham': 'RDU',
  'PARADIES - MEMPHIS': 'MEM',
  'MEMPHIS': 'MEM',
  'CAE - Columbia': 'CAE',
  'Columbia': 'CAE',
  'GSO - Greensboro': 'GSO',
  'Greensboro': 'GSO',
  'Nashville': 'BNA',
  'PARADIES - PENSACOLA': 'PNS',
  'PENSACOLA': 'PNS',
  'Pensacola': 'PNS',
  'Asheville Regional Airport': 'ASH',
  'Asheville': 'ASH',
  'ShipTo/Fro 61 Terminal': 'ATL',  // Need to verify this mapping
  'Fletcher': 'FLE',
};

// Helper function to find platform code from destination string
const findPlatformCode = (destinationStr) => {
  if (!destinationStr) return null;
  
  const destination = destinationStr.toString().trim();
  
  // Check for exact matches first
  for (const [key, code] of Object.entries(DESTINATION_TO_PLATFORM)) {
    if (destination.includes(key)) {
      return code;
    }
  }
  
  // If no match, try to extract airport code (3-4 letters)
  const airportCodeMatch = destination.match(/\b([A-Z]{3,4})\b/);
  if (airportCodeMatch) {
    return airportCodeMatch[1];
  }
  
  return null;
};

// KPI Drill-Down Configuration
// Determines whether each KPI shows trend (historical) or breakdown (by platform) view
const KPI_DRILL_DOWN_CONFIG = {
  // Trend views (historical performance over time)
  vendorFillRate: 'trend',
  lphPicking: 'trend',
  pickAccuracy: 'trend',
  automationUptime: 'trend',
  inventoryAccuracy: 'trend',
  scheduleScore: 'trend',
  
  // Breakdown views (performance by platform/location)
  vendorsConvertedSales: 'breakdown',
  vendorsConvertedCount: 'breakdown',
  onTimeDelivery: 'breakdown',
  receivingUph: 'breakdown',
  complete4hrs: 'breakdown',
  inventoryAccuracyPlatform: 'breakdown',
  skuInStockPlatform: 'breakdown',
  
  // Cost metrics - trend views to show cost evolution
  dockToStock: 'trend',
  storageUtilization: 'trend',
  fourWallCost: 'trend',
  deliveryCost: 'trend',
  totalCombinedCost: 'trend',
  costCompareBase: 'trend',
  varianceBudget: 'trend',
  percentTotalSales: 'trend',
  
  // Mixed - warehouse metrics could go either way, defaulting to breakdown
  outboundServiceLevel: 'breakdown',
  warehouseInStock: 'breakdown',
  warehouseInventory: 'breakdown'
};

// KPI Calculation Descriptions
// Placeholder text for calculation methodology tooltips
const KPI_CALCULATIONS = {
  vendorFillRate: "The raw cut fill rate to PARADIES ATLANTA 9678 W910 as stated in the Weekly Fill Rate Report (Column H of 'By Platform' tab)",
  vendorsConvertedSales: "percentage of units sold that the currently converted vendors account for (vistar, wolf, and electronics)",
  vendorsConvertedCount: "Number of successfully converted vendors",
  dockToStock: "Time required to put received goods away for picking",
  lphPicking: "Calculated from OBD sign-in sheets",
  pickAccuracy: "Quantity picked divided by quantity ordered",
  outboundServiceLevel: "Binary on-time metric with 30-minute departure grace period",
  inventoryAccuracy: "Measured through cycle counting process",
  skuInStock: "Based on warehouse on-hand report",
  storageUtilization: "Used bays divided by total bays",
  automationUptime: "⚠️ PLACEHOLDER: Calculation methodology to be provided",
  fourWallCost: "⚠️ PLACEHOLDER: Calculation methodology to be provided",
  onTimeDelivery: "Truck arrival at platform within 30-minute window",
  receivingUph: "Receiving and putaway speed after truck arrival",
  complete4hrs: "Percentage of times platform completes putaway within 4 hours of truck arrival",
  inventoryAccuracyPlatform: "Measured through lows and outs process",
  skuInStockPlatform: "Systemic in-stock levels for travel essential stores with min/max values",
  scheduleScore: "⚠️ PLACEHOLDER: Calculation methodology to be provided",
  deliveryCost: "Outbound cost from P&L divided by total monthly deliveries",
  totalCombinedCost: "⚠️ PLACEHOLDER: Calculation methodology to be provided",
  costCompareBase: "4-wall cost (excluding outbound) divided by total quantity picked for the month",
  varianceBudget: "Total cost compared to budget/E",
  percentTotalSales: "Total cost divided by total sales for all platform stores in service region",
  warehouseInventory: "Total unique SKUs in warehouse W004 on-hand inventory with total cost value"
};

/**
 * KPI COLOR CODING SYSTEM
 * 
 * Threshold: 15% variance from goal determines yellow vs red
 * 
 * Standard Metrics (Higher is Better):
 *   Green: >= Goal
 *   Yellow: >= Goal * 0.85 (within 15% below goal)
 *   Red: < Goal * 0.85 (more than 15% below goal)
 * 
 * Cost Metrics (Lower is Better):
 *   Green: <= Goal
 *   Yellow: <= Goal * 1.15 (within 15% above goal)
 *   Red: > Goal * 1.15 (more than 15% above goal)
 * 
 * TODO: Make threshold configurable per KPI in future
 */

/**
 * Determines the color for a KPI tile based on current value vs goal
 * @param {number} current - Current KPI value
 * @param {number} goal - Target goal value
 * @param {boolean} lowerIsBetter - True for cost metrics where lower is better
 * @returns {string} - Material-UI color: 'success', 'warning', or 'error'
 */
const getKpiColor = (current, goal, lowerIsBetter = false) => {
  // Handle missing data
  if (!current || !goal) return '#757575'; // Gray for N/A
  
  const currentNum = parseFloat(current);
  const goalNum = parseFloat(goal);
  
  if (lowerIsBetter) {
    // For cost metrics - lower is better
    if (currentNum <= goalNum) return '#2e7d32'; // Green - meeting goal
    if (currentNum <= goalNum * 1.15) return '#ed6c02'; // Yellow - within 15%
    return '#d32f2f'; // Red - far from goal
  } else {
    // For standard metrics - higher is better
    if (currentNum >= goalNum) return '#2e7d32'; // Green - meeting goal
    if (currentNum >= goalNum * 0.85) return '#ed6c02'; // Yellow - within 15%
    return '#d32f2f'; // Red - far from goal
  }
};

const KPITracking = () => {
  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [detectedReportType, setDetectedReportType] = useState(null);
  const [extractedData, setExtractedData] = useState(null);

  // Multi-file upload state
  const [uploadProgress, setUploadProgress] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // KPI values state
  const [kpiValues, setKpiValues] = useState({
    vendorFillRate: null,
    currentWeek: null,
    warehouseInStock: null,
    totalSkuCount: null,
    onTimeDelivery: null,
    totalDeliveries: null,
    // Add all manually editable KPI fields
    vendorsConvertedSales: null,
    vendorsConvertedCount: null,
    dockToStock: null,
    lphPicking: null,
    pickAccuracy: null,
    outboundServiceLevel: null,
    inventoryAccuracy: null,
    storageUtilization: null,
    automationUptime: null,
    fourWallCost: null,
    receivingUph: null,
    complete4hrs: null,
    inventoryAccuracyPlatform: null,
    skuInStockPlatform: null,
    scheduleScore: null,
    deliveryCost: null,
    totalCombinedCost: null,
    costCompareBase: null,
    varianceBudget: null,
    percentTotalSales: null,
    warehouseInventory: null,
    warehouseInventoryCost: null
  });

  // Warehouse inventory data state
  const [warehouseInventoryData, setWarehouseInventoryData] = useState(null);

  // KPI goals state
  const [kpiGoals, setKPIGoals] = useState({});
  
  // Loading states
  const [isLoadingKpis, setIsLoadingKpis] = useState(true);
  const [isLoadingGoals, setIsLoadingGoals] = useState(true);

  // Drill-down modal state
  const [drillDownModal, setDrillDownModal] = useState({
    open: false,
    kpiKey: null,
    kpiLabel: null,
    drillDownType: null,
    data: null
  });

  // Load KPI values from API on mount
  useEffect(() => {
    const loadKpiValues = async () => {
      try {
        const data = await api.getKpiValues();
        console.log('📊 Loaded KPI values from API:', data);
        
        // Convert database snake_case to camelCase for ALL KPI fields
        const formattedData = {
          vendorFillRate: data.vendor_fill_rate,
          currentWeek: data.current_week,
          warehouseInStock: data.warehouse_in_stock,
          totalSkuCount: data.total_sku_count,
          onTimeDelivery: data.on_time_delivery,
          totalDeliveries: data.total_deliveries,
          // Add all manually editable KPI fields
          vendorsConvertedSales: data.vendors_converted_sales,
          vendorsConvertedCount: data.vendors_converted_count,
          dockToStock: data.dock_to_stock,
          lphPicking: data.lph_picking,
          pickAccuracy: data.pick_accuracy,
          outboundServiceLevel: data.outbound_service_level,
          inventoryAccuracy: data.inventory_accuracy,
          storageUtilization: data.storage_utilization,
          automationUptime: data.automation_uptime,
          fourWallCost: data.four_wall_cost,
          receivingUph: data.receiving_uph,
          complete4hrs: data.complete_4hrs,
          inventoryAccuracyPlatform: data.inventory_accuracy_platform,
          skuInStockPlatform: data.sku_in_stock_platform,
          scheduleScore: data.schedule_score,
          deliveryCost: data.delivery_cost,
          totalCombinedCost: data.total_combined_cost,
          costCompareBase: data.cost_compare_base,
          varianceBudget: data.variance_budget,
          percentTotalSales: data.percent_total_sales
        };
        
        setKpiValues(prev => {
          const updated = {
            ...formattedData,
            // Preserve computed values that don't come from API
            warehouseInventory: prev.warehouseInventory,
            warehouseInventoryCost: prev.warehouseInventoryCost
          };
          console.log('🔄 API data loading - preserving warehouseInventory:', prev.warehouseInventory, '→', updated.warehouseInventory);
          return updated;
        });
      } catch (error) {
        console.error('Error loading KPI values:', error);
      } finally {
        setIsLoadingKpis(false);
      }
    };

    const loadKPIGoals = async () => {
      try {
        const data = await api.getKpiGoals();
        console.log('🎯 Loaded KPI goals from API:', data);
        
        // Convert snake_case to camelCase
        const formattedGoals = {
          vendorFillRate: data.vendor_fill_rate,
          skuInStock: data.sku_in_stock,
          onTimeDelivery: data.on_time_delivery,
          vendorsConvertedSales: data.vendors_converted_sales,
          vendorsConvertedCount: data.vendors_converted_count,
          dockToStock: data.dock_to_stock,
          lphPicking: data.lph_picking,
          pickAccuracy: data.pick_accuracy,
          outboundServiceLevel: data.outbound_service_level,
          inventoryAccuracy: data.inventory_accuracy,
          storageUtilization: data.storage_utilization,
          automationUptime: data.automation_uptime,
          fourWallCost: data.four_wall_cost,
          receivingUph: data.receiving_uph,
          complete4hrs: data.complete_4hrs,
          inventoryAccuracyPlatform: data.inventory_accuracy_platform,
          skuInStockPlatform: data.sku_in_stock_platform,
          scheduleScore: data.schedule_score,
          deliveryCost: data.delivery_cost,
          totalCombinedCost: data.total_combined_cost,
          costCompareBase: data.cost_compare_base,
          varianceBudget: data.variance_budget,
          percentTotalSales: data.percent_total_sales
        };
        
        console.log('🎯 Formatted goals:', formattedGoals);
        setKPIGoals(formattedGoals);
      } catch (error) {
        console.error('Error loading KPI goals:', error);
      } finally {
        setIsLoadingGoals(false);
      }
    };

    loadKpiValues();
    loadKPIGoals();
    loadWarehouseInventory();
  }, []);

  // Load warehouse inventory data
  const loadWarehouseInventory = async () => {
    console.log('🔄 Loading warehouse inventory...');
    try {
      const data = await processWarehouseInventory();
      console.log('📦 Received inventory data:', data);
      
      setWarehouseInventoryData(data);
      
      // Update KPI values with inventory count and cost
      if (data.totalSkus) {
        setKpiValues(prev => {
          console.log('🔄 Previous kpiValues before inventory update:', prev.warehouseInventory);
          const updated = {
            ...prev,
            warehouseInventory: data.totalSkus,
            warehouseInventoryCost: data.totalCostValue
          };
          console.log('📊 Updated KPI values with inventory:', updated.warehouseInventory);
          console.log('💰 Updated KPI values with cost:', updated.warehouseInventoryCost);
          console.log('✅ Setting warehouseInventory to:', data.totalSkus);
          return updated;
        });
      } else {
        console.warn('⚠️ No totalSkus in inventory data:', data);
      }
      console.log('📦 Warehouse inventory loaded:', data.totalSkus, 'unique SKUs');
    } catch (error) {
      console.error('❌ Error loading warehouse inventory:', error);
      console.error('Error details:', error.message, error.stack);
    }
  };

  // Report detection functions - check workbook structure
  const detectReportType = (workbook, filename = '') => {
    console.log('🔍 Detecting report type for file:', filename);
    
    // PRIORITY 1: Check filename patterns first (more reliable)
    const lowerFilename = filename.toLowerCase();
    
    // Vistar Fill Rate Reports
    if (lowerFilename.includes('fill rate') || 
        lowerFilename.includes('weekly fill rate') ||
        lowerFilename.includes('operator view') ||
        lowerFilename.includes('vistar') ||
        lowerFilename.includes('vendor fill')) {
      console.log('📄 Filename pattern indicates: Vendor Stated Fill Rate Report');
      return 'Vendor Stated Fill Rate Report';
    }
    
    // Zero On Hands Reports
    if (lowerFilename.includes('zero on hand') || 
        lowerFilename.includes('zero inventory') ||
        lowerFilename.includes('stock out')) {
      console.log('📄 Filename pattern indicates: Zero On Hands Report');
      return 'Zero On Hands Report';
    }
    
    // Delivery Reports
    if (lowerFilename.includes('delivery') || 
        lowerFilename.includes('on time') ||
        lowerFilename.includes('shipment')) {
      console.log('📄 Filename pattern indicates: Delivery On-Time Report');
      return 'Delivery On-Time Report';
    }
    
    // PRIORITY 2: Check sheet names if filename doesn't match
    const sheetNames = workbook.SheetNames || [];
    console.log('📋 Checking sheet names:', sheetNames);
    
    // Fill Rate Report has a "Tracker" tab OR "By Platform" tab (new format)
    if (sheetNames.includes('Tracker') || sheetNames.includes('By Platform')) {
      console.log('📋 Sheet structure indicates: Vendor Stated Fill Rate Report');
      return 'Vendor Stated Fill Rate Report';
    }
    
    // Zero On Hands has DASHBOARD_W910 and DASHBOARD_W004 tabs
    if (sheetNames.includes('DASHBOARD_W910') && sheetNames.includes('DASHBOARD_W004')) {
      return 'Zero On Hands Report';
    }
    
    // Fallback: check first sheet column headers
    const firstSheet = workbook.Sheets[sheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
    
    if (jsonData.length > 0) {
      const headers = jsonData[0] || [];
      const headerText = headers.join(' ').toLowerCase();
      
      // Delivery Report - check for delivery-related columns
      if (headerText.includes('dest. date') && 
          (headerText.includes('departure') || headerText.includes('arrival'))) {
        return 'Delivery On-Time Report';
      }
      
      if (headerText.includes('platform') && headerText.includes('brand')) {
        return 'Vendor Stated Fill Rate Report';
      }
      
      if (headerText.includes('material') && headerText.includes('zero')) {
        return 'Zero On-Hand Inventory Report';
      }
      
      if (headerText.includes('cargo') || headerText.includes('ship')) {
        return 'Shipment Tracking Report';
      }
    }
    
    return 'Unknown Report Type';
  };

  const extractKPIData = (reportType, data) => {
    const extracted = {};
    
    switch (reportType) {
      case 'Vendor Stated Fill Rate Report':
        // Extract fill rate metrics
        const totalSales = data.reduce((sum, row) => {
          const lastCol = Object.keys(row)[Object.keys(row).length - 1];
          const value = parseFloat(row[lastCol]) || 0;
          return sum + value;
        }, 0);
        
        extracted.totalSales = totalSales;
        extracted.platformCount = new Set(data.map(row => row.Platform || row.platform)).size;
        extracted.reportDate = new Date().toISOString();
        break;
        
      case 'Zero On-Hand Inventory Report':
        // Count zero on-hand items
        extracted.zeroOnHandCount = data.length;
        extracted.affectedStores = new Set(data.map(row => 
          Object.values(row)[0] || 'Unknown'
        )).size;
        break;
        
      case 'Shipment Tracking Report':
        // Count shipments
        extracted.shipmentCount = data.length;
        extracted.locations = new Set(data.map(row => 
          Object.values(row).find(val => 
            typeof val === 'string' && val.includes('PARADIES')
          )
        )).size;
        break;
        
      default:
        extracted.rowCount = data.length;
        extracted.columnCount = Object.keys(data[0] || {}).length;
    }
    
    return extracted;
  };

  // Drill-down modal handlers
  const handleKpiClick = (kpiKey, kpiLabel, drillDownType) => {
    console.log('🔍 Opening drill-down for:', kpiLabel, '- Type:', drillDownType);
    
    // Special handling for warehouse inventory
    if (kpiKey === 'warehouseInventory' && warehouseInventoryData) {
      setDrillDownModal({
        open: true,
        kpiKey,
        kpiLabel,
        drillDownType,
        data: {
          type: 'by_material_group',
          data: warehouseInventoryData.drillDownData
        }
      });
      return;
    }
    
    // Check if we have hardcoded demo data for this KPI
    if (DEMO_DRILLDOWN_DATA[kpiKey]) {
      setDrillDownModal({
        open: true,
        kpiKey,
        kpiLabel,
        drillDownType,
        data: DEMO_DRILLDOWN_DATA[kpiKey]
      });
    } else {
      // Use existing synthetic data generation
      setDrillDownModal({ 
        open: true, 
        kpiKey, 
        kpiLabel, 
        drillDownType 
      });
    }
  };

  const handleCloseDrillDown = () => {
    setDrillDownModal({ 
      open: false, 
      kpiKey: null, 
      kpiLabel: null, 
      drillDownType: null,
      data: null
    });
  };

  const handleMultiFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    console.log(`📦 Processing ${files.length} file(s)...`);
    
    setIsProcessing(true);
    setUploadStatus(null);
    setUploadedFile(null);
    setDetectedReportType(null);
    
    // Initialize progress tracking
    const progressList = files.map(file => ({
      name: file.name,
      status: 'pending',
      reportType: null,
      error: null
    }));
    setUploadProgress(progressList);
    
    // Process each file
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      console.log(`\n📄 Processing file ${i + 1}/${files.length}: ${file.name}`);
      
      // Update progress - processing
      setUploadProgress(prev => prev.map((item, idx) => 
        idx === i ? { ...item, status: 'processing' } : item
      ));
      
      try {
        await processFile(file, i, results);
      } catch (error) {
        console.error(`❌ Error processing ${file.name}:`, error);
        results.push({
          fileName: file.name,
          reportType: null,
          error: error.message,
          success: false
        });
        
        // Update progress - error
        setUploadProgress(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'error', error: error.message } : item
        ));
      }
    }
    
    // Aggregate all KPI data
    await updateAllKPIs(results);
    
    // Clear file input
    event.target.value = '';
  };

  // Helper function to process individual file
  const processFile = async (file, index, results) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    
    // First try filename-based detection
    const reportType = detectReportType(workbook, file.name);
    console.log(`✅ File ${index + 1} (${file.name}) detected as: ${reportType}`);
    
    let kpiData = {};
    
    // Route to appropriate parser
    if (reportType === 'Vendor Stated Fill Rate Report') {
      kpiData = await parseFillRateFile(workbook);
    } else if (reportType === 'Zero On Hands Report') {
      kpiData = await parseZeroOnHandsFile(workbook);
    } else if (reportType === 'Delivery On-Time Report') {
      kpiData = await parseDeliveryOnTimeFile(workbook);
    } else {
      throw new Error(`Unknown report type: ${reportType}`);
    }
    
    results.push({
      fileName: file.name,
      reportType: reportType,
      data: kpiData,
      success: true
    });
    
    // Update progress - success
    setUploadProgress(prev => prev.map((item, idx) => 
      idx === index ? { ...item, status: 'success', reportType: reportType } : item
    ));
  };

  // Helper function to aggregate and save all KPI data
  const updateAllKPIs = async (results) => {
    const aggregatedKpiValues = { ...kpiValues };
    
    results.forEach(result => {
      if (result.success) {
        if (result.reportType === 'Vendor Stated Fill Rate Report') {
          aggregatedKpiValues.vendorFillRate = result.data.vendorFillRate;
          aggregatedKpiValues.currentWeek = result.data.currentWeek;
        } else if (result.reportType === 'Zero On Hands Report') {
          aggregatedKpiValues.warehouseInStock = result.data.warehouseInStock;
          aggregatedKpiValues.totalSkuCount = result.data.totalCount;
        } else if (result.reportType === 'Delivery On-Time Report') {
          aggregatedKpiValues.outboundServiceLevel = result.data.outboundDeparture;  // KPI 1: Outbound Departure
          aggregatedKpiValues.onTimeDelivery = result.data.onTimeDelivery;           // KPI 2: On Time Delivery (Arrival)
          aggregatedKpiValues.totalDeliveries = result.data.totalDeliveries;
        }
      }
    });
    
    // Update state with all KPI data at once
    setKpiValues(aggregatedKpiValues);
    
    // Save to API
    try {
      await api.updateKpiValues({
        vendor_fill_rate: aggregatedKpiValues.vendorFillRate,
        current_week: aggregatedKpiValues.currentWeek,
        warehouse_in_stock: aggregatedKpiValues.warehouseInStock,
        total_sku_count: aggregatedKpiValues.totalSkuCount,
        outbound_service_level: aggregatedKpiValues.outboundServiceLevel,  // KPI 1: Outbound Departure
        on_time_delivery: aggregatedKpiValues.onTimeDelivery,              // KPI 2: On Time Delivery (Arrival)
        total_deliveries: aggregatedKpiValues.totalDeliveries
      });
      console.log('💾 All KPI values saved to API');
    } catch (error) {
      console.error('Error saving KPI values to API:', error);
    }
    
    console.log('💾 All KPI values updated:', aggregatedKpiValues);
    
    // Generate summary message
    const successCount = results.filter(r => r.success).length;
    const errorCount = results.filter(r => !r.success).length;
    
    let summaryMessage = `Processed ${results.length} file(s): `;
    summaryMessage += `${successCount} successful`;
    if (errorCount > 0) {
      summaryMessage += `, ${errorCount} failed`;
    }
    
    setUploadStatus({
      type: errorCount === 0 ? 'success' : 'warning',
      message: summaryMessage
    });
    
    setIsProcessing(false);
  };

  // Keep single file upload for backward compatibility
  const handleSingleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    setUploadStatus({ type: 'info', message: 'Reading file...' });
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      console.log('📄 Workbook loaded with sheets:', workbook.SheetNames);

      // Detect report type by checking sheets and structure
      const reportType = detectReportType(workbook);
      setDetectedReportType(reportType);

      console.log('✅ Detected report type:', reportType);

      // Parse based on report type
      let kpiData = {};

      if (reportType === 'Vendor Stated Fill Rate Report') {
        kpiData = await parseFillRateFile(workbook);
      } else if (reportType === 'Zero On Hands Report') {
        kpiData = await parseZeroOnHandsFile(workbook);
      } else if (reportType === 'Delivery On-Time Report') {
        kpiData = await parseDeliveryOnTimeFile(workbook);
      } else {
        // Generic fallback
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        kpiData = extractKPIData(reportType, jsonData);
      }
      
      setExtractedData(kpiData);

      // Update KPI values state with extracted data
      if (reportType === 'Vendor Stated Fill Rate Report') {
        const updatedValues = {
          ...kpiValues,
          vendorFillRate: kpiData.vendorFillRate,
          currentWeek: kpiData.currentWeek
        };
        setKpiValues(updatedValues);
        
        // Save to API
        try {
          await api.updateKpiValues({
            vendor_fill_rate: updatedValues.vendorFillRate,
            current_week: updatedValues.currentWeek,
            warehouse_in_stock: updatedValues.warehouseInStock,
            total_sku_count: updatedValues.totalSkuCount,
            on_time_delivery: updatedValues.onTimeDelivery,
            total_deliveries: updatedValues.totalDeliveries
          });
          console.log('💾 Saved KPI values to API:', updatedValues);
        } catch (error) {
          console.error('Error saving KPI values to API:', error);
        }
      } else if (reportType === 'Zero On Hands Report') {
        const updatedValues = {
          ...kpiValues,
          warehouseInStock: kpiData.warehouseInStock,
          totalSkuCount: kpiData.totalCount
        };
        setKpiValues(updatedValues);
        
        // Save to API
        try {
          await api.updateKpiValues({
            vendor_fill_rate: updatedValues.vendorFillRate,
            current_week: updatedValues.currentWeek,
            warehouse_in_stock: updatedValues.warehouseInStock,
            total_sku_count: updatedValues.totalSkuCount,
            on_time_delivery: updatedValues.onTimeDelivery,
            total_deliveries: updatedValues.totalDeliveries
          });
          console.log('💾 Saved KPI values to API:', updatedValues);
        } catch (error) {
          console.error('Error saving KPI values to API:', error);
        }
      } else if (reportType === 'Delivery On-Time Report') {
        const updatedValues = {
          ...kpiValues,
          outboundServiceLevel: kpiData.outboundDeparture,  // KPI 1: Outbound Departure
          onTimeDelivery: kpiData.onTimeDelivery,           // KPI 2: On Time Delivery (Arrival)
          totalDeliveries: kpiData.totalDeliveries
        };
        setKpiValues(updatedValues);
        
        // Save to API
        try {
          await api.updateKpiValues({
            vendor_fill_rate: updatedValues.vendorFillRate,
            current_week: updatedValues.currentWeek,
            warehouse_in_stock: updatedValues.warehouseInStock,
            total_sku_count: updatedValues.totalSkuCount,
            outbound_service_level: updatedValues.outboundServiceLevel,  // KPI 1: Outbound Departure
            on_time_delivery: updatedValues.onTimeDelivery,              // KPI 2: On Time Delivery (Arrival)
            total_deliveries: updatedValues.totalDeliveries
          });
          console.log('💾 Saved KPI values to API:', updatedValues);
        } catch (error) {
          console.error('Error saving KPI values to API:', error);
        }
      }
      
      setUploadStatus({ 
        type: 'success', 
        message: reportType === 'Vendor Stated Fill Rate Report'
          ? `Successfully processed ${reportType}! Current Fill Rate: ${kpiData.vendorFillRate}% (Week ${kpiData.currentWeek})`
          : reportType === 'Zero On Hands Report'
          ? `Successfully processed ${reportType}! Warehouse In-Stock: ${kpiData.warehouseInStock}% (${kpiData.totalCount} SKUs)`
          : reportType === 'Delivery On-Time Report'
          ? `Successfully processed ${reportType}! Outbound Departure: ${kpiData.outboundDeparture}%, On-Time Delivery: ${kpiData.onTimeDelivery}% (${kpiData.totalDeliveries} deliveries)`
          : `Successfully processed ${reportType}! Found ${Object.keys(kpiData).length} data points.`
      });
      
      // TODO: Update KPI dashboard with extracted data
      console.log('📊 Extracted KPI data:', kpiData);
      
    } catch (error) {
      console.error('Error processing file:', error);
      setUploadStatus({ 
        type: 'error', 
        message: `Error processing file: ${error.message}` 
      });
    }
  };

  const parseFillRateFile = async (workbook) => {
    try {
      console.log('📊 Processing Vendor Stated Fill Rate Report...');
      
      // Get the SECOND tab (index 1) - "By Platform"
      const sheetNames = workbook.SheetNames;
      console.log('📋 Available sheets:', sheetNames);
      
      if (sheetNames.length < 2) {
        throw new Error('File must have at least 2 tabs. Expected "By Platform" as second tab.');
      }
      
      const byPlatformSheetName = sheetNames[1]; // Second tab
      const byPlatformSheet = workbook.Sheets[byPlatformSheetName];
      
      if (!byPlatformSheet) {
        throw new Error(`Second sheet "${byPlatformSheetName}" not found in workbook`);
      }
      
      console.log('📋 Reading second sheet:', byPlatformSheetName);
      
      // Convert sheet to JSON for easier processing
      const platformData = XLSX.utils.sheet_to_json(byPlatformSheet);
      console.log('📊 Platform data loaded, rows:', platformData.length);
      
      if (platformData.length === 0) {
        throw new Error('No data found in "By Platform" sheet');
      }
      
      // Log all available platforms for debugging
      console.log('📋 Available platforms in file:');
      const allPlatforms = platformData.map((row, index) => {
        const platform = row['Platform'] || row['Acct #'] || row['Account'] || row['Store'] || Object.values(row)[1];
        if (platform) {
          console.log(`  Row ${index + 1}: "${platform}"`);
        }
        return platform;
      }).filter(p => p);

      console.log(`Found ${allPlatforms.length} platform entries`);
      console.log('🔍 Looking for platform matching: PARADIES ATLANTA 9678 W910');

      // More flexible search - trim whitespace, case-insensitive, partial match
      const targetRow = platformData.find(row => {
        const platform = (row['Platform'] || row['Acct #'] || row['Account'] || row['Store'] || Object.values(row)[1] || '').toString().trim();
        
        // Try multiple matching strategies
        const matches = 
          platform === 'PARADIES ATLANTA 9678 W910' || // Exact match
          platform.includes('ATLANTA 9678 W910') ||     // Partial match
          platform.includes('9678 W910') ||             // Store number match
          platform.toUpperCase().includes('ATLANTA 9678') || // Case-insensitive
          platform.includes('PARADIES ATLANTA'); // Company match
        
        if (matches) {
          console.log('✅ Found matching platform:', platform);
        }
        
        return matches;
      });

      if (!targetRow) {
        console.error('❌ Could not find platform matching "PARADIES ATLANTA 9678 W910"');
        console.error('Available platforms:', allPlatforms.slice(0, 10)); // Show first 10
        throw new Error(`Could not find platform "PARADIES ATLANTA 9678 W910" in file. Found ${allPlatforms.length} platforms total.`);
      }
      
      console.log('✅ Found target platform row:', targetRow);
      
      // Extract Fill Ratio from Column H
      let fillRate = targetRow['Fill Ratio'] || targetRow['Fill Rate'] || targetRow['FillRatio'];
      
      // If not found by column name, try to get Column H by position
      if (!fillRate) {
        const rowKeys = Object.keys(targetRow);
        fillRate = targetRow[rowKeys[7]]; // Column H is index 7 (A=0, B=1, ..., H=7)
      }
      
      if (!fillRate && fillRate !== 0) {
        console.error('❌ Available columns:', Object.keys(targetRow));
        throw new Error('Could not find Fill Ratio/Fill Rate value in Column H');
      }
      
      console.log('📊 Raw Fill Ratio value:', fillRate);

      // Excel stores percentages as decimals (0.9687 = 96.87%)
      // If the value is between 0 and 1, it's likely already a decimal that needs to be converted
      fillRate = parseFloat(fillRate);

      if (isNaN(fillRate)) {
        throw new Error('Fill Ratio is not a valid number');
      }

      // If value is between 0 and 1, it's a decimal percentage - multiply by 100
      // If value is already > 1, it's already a percentage
      if (fillRate > 0 && fillRate < 1) {
        fillRate = fillRate * 100;
        console.log('📊 Converted decimal to percentage:', fillRate);
      } else if (fillRate >= 1 && fillRate <= 100) {
        console.log('📊 Value already in percentage format:', fillRate);
      } else {
        console.warn('⚠️ Unexpected fill rate value:', fillRate);
      }

      // Round to 2 decimal places
      fillRate = Math.round(fillRate * 100) / 100;

      console.log('✅ Final Fill Rate value:', fillRate);
      
      return {
        vendorFillRate: fillRate.toFixed(2),
        currentWeek: new Date().getWeek(), // Current week
        platform: 'PARADIES ATLANTA 9678 W910',
        sheetUsed: byPlatformSheetName,
        extractedValue: fillRate
      };
      
    } catch (error) {
      console.error('❌ Error parsing Vendor Stated Fill Rate file:', error);
      throw error; // Re-throw to trigger error handling in calling function
    }
  };

  // Helper function to get current week number
  Date.prototype.getWeek = function() {
    const date = new Date(this.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };

  const parseZeroOnHandsFile = async (workbook) => {
    try {
      console.log('📊 Parsing Zero On Hands file...');
      
      // Parse W910 tab
      const w910Sheet = workbook.Sheets['DASHBOARD_W910'];
      if (!w910Sheet) {
        throw new Error('DASHBOARD_W910 tab not found');
      }
      
      // Get in-stock % from cell AD9
      const w910InStockCell = w910Sheet['AD9'];
      console.log('🔍 W910 Cell AD9 raw:', w910InStockCell);
      console.log('🔍 W910 Cell AD9 value (v):', w910InStockCell?.v);
      console.log('🔍 W910 Cell AD9 formatted (w):', w910InStockCell?.w);
      console.log('🔍 W910 Cell AD9 type (t):', w910InStockCell?.t);

      let w910InStockPercent = null;
      if (w910InStockCell) {
        let cellValue = w910InStockCell.v || w910InStockCell.w;
        
        if (cellValue !== undefined && cellValue !== null) {
          // Remove formula prefix and percentage sign
          const cleanValue = cellValue.toString().replace('=', '').replace('%', '').trim();
          const numValue = parseFloat(cleanValue);
          
          if (!isNaN(numValue)) {
            // Value is already a percentage (like 99.51)
            w910InStockPercent = numValue;
            console.log('✅ W910 In-Stock % parsed:', w910InStockPercent + '%');
          }
        }
      }
      console.log('📍 W910 In-Stock % (AD9):', w910InStockPercent);
      
      // Find Article Count in W910 AVAILABILITY table - search dynamically
      const w910Data = XLSX.utils.sheet_to_json(w910Sheet, { header: 1 });
      let w910ArticleCount = null;

      console.log('🔍 W910 sheet has', w910Data.length, 'rows');
      console.log('🔍 Searching for "Article Count" in W910...');

      // Search ALL rows starting from row 13 (index 12)
      for (let i = 12; i < w910Data.length && i < 50; i++) { // Limit to first 50 rows
        const row = w910Data[i] || [];
        
        // Search through ALL columns in this row
        for (let colIdx = 0; colIdx < row.length; colIdx++) {
          const cellValue = row[colIdx];
          
          if (cellValue && cellValue.toString().trim() === 'Article Count') {
            // Found it! Value should be in the NEXT column (same row)
            const valueCell = row[colIdx + 1];
            w910ArticleCount = parseFloat(valueCell);
            
            console.log(`✅ W910 "Article Count" found at row ${i + 1}, column index ${colIdx}`);
            console.log(`✅ W910 Article Count value (from column ${colIdx + 1}):`, w910ArticleCount);
            break;
          }
        }
        
        if (w910ArticleCount) break; // Exit outer loop once found
      }

      if (!w910ArticleCount) {
        console.warn('⚠️ W910 Article Count not found');
      }
      
      // Parse W004 tab
      const w004Sheet = workbook.Sheets['DASHBOARD_W004'];
      if (!w004Sheet) {
        throw new Error('DASHBOARD_W004 tab not found');
      }
      
      // Get in-stock % from cell U10
      const w004InStockCell = w004Sheet['U10'];
      console.log('🔍 W004 Cell U10 raw:', w004InStockCell);
      console.log('🔍 W004 Cell U10 value (v):', w004InStockCell?.v);
      console.log('🔍 W004 Cell U10 formatted (w):', w004InStockCell?.w);
      console.log('🔍 W004 Cell U10 type (t):', w004InStockCell?.t);

      let w004InStockPercent = null;
      if (w004InStockCell) {
        let cellValue = w004InStockCell.v || w004InStockCell.w;
        
        if (cellValue !== undefined && cellValue !== null) {
          // Remove formula prefix and percentage sign
          const cleanValue = cellValue.toString().replace('=', '').replace('%', '').trim();
          const numValue = parseFloat(cleanValue);
          
          if (!isNaN(numValue)) {
            w004InStockPercent = numValue;
            console.log('✅ W004 In-Stock % parsed:', w004InStockPercent + '%');
          }
        }
      }
      console.log('📍 W004 In-Stock % (U10):', w004InStockPercent);
      
      // Find Article Count in W004 AVAILABILITY table - search dynamically
      const w004Data = XLSX.utils.sheet_to_json(w004Sheet, { header: 1 });
      let w004ArticleCount = null;

      console.log('🔍 W004 sheet has', w004Data.length, 'rows');
      console.log('🔍 Searching for "Article Count" in W004...');

      // Search ALL rows
      for (let i = 0; i < w004Data.length && i < 50; i++) {
        const row = w004Data[i] || [];
        
        // Search through ALL columns in this row
        for (let colIdx = 0; colIdx < row.length; colIdx++) {
          const cellValue = row[colIdx];
          
          if (cellValue && cellValue.toString().trim() === 'Article Count') {
            // Found it! Value should be in the NEXT column (same row)
            const valueCell = row[colIdx + 1];
            w004ArticleCount = parseFloat(valueCell);
            
            console.log(`✅ W004 "Article Count" found at row ${i + 1}, column index ${colIdx}`);
            console.log(`✅ W004 Article Count value (from column ${colIdx + 1}):`, w004ArticleCount);
            break;
          }
        }
        
        if (w004ArticleCount) break; // Exit outer loop once found
      }

      if (!w004ArticleCount) {
        console.warn('⚠️ W004 Article Count not found');
      }
      
      // Calculate weighted average in-stock %
      let weightedInStock = null;
      
      if (w910InStockPercent !== null && w004InStockPercent !== null && 
          w910ArticleCount && w004ArticleCount) {
        
        const totalArticles = w910ArticleCount + w004ArticleCount;
        weightedInStock = (
          (w910InStockPercent * w910ArticleCount) + 
          (w004InStockPercent * w004ArticleCount)
        ) / totalArticles;
        
        console.log('📊 Weighted Average Calculation:');
        console.log(`  W910: ${w910InStockPercent.toFixed(1)}% × ${w910ArticleCount} SKUs`);
        console.log(`  W004: ${w004InStockPercent.toFixed(1)}% × ${w004ArticleCount} SKUs`);
        console.log(`  Total: ${weightedInStock.toFixed(1)}% (${totalArticles} SKUs)`);
      }
      
      // Store historical data (could be saved to API in future)
      const zeroOnHandsData = {
        uploadDate: new Date().toISOString(),
        w910: {
          inStockPercent: w910InStockPercent,
          articleCount: w910ArticleCount
        },
        w004: {
          inStockPercent: w004InStockPercent,
          articleCount: w004ArticleCount
        },
        weightedInStock: weightedInStock
      };
      
      // Note: Historical data could be stored via API in future iterations
      console.log('📊 Zero on hands historical data:', zeroOnHandsData);
      
      return {
        warehouseInStock: weightedInStock?.toFixed(1) || 'N/A',
        w910Count: w910ArticleCount,
        w004Count: w004ArticleCount,
        totalCount: (w910ArticleCount || 0) + (w004ArticleCount || 0)
      };
      
    } catch (error) {
      console.error('Error parsing Zero On Hands file:', error);
      return { warehouseInStock: 'Error', totalCount: 0 };
    }
  };

  // Helper function to convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12) => {
    if (!time12) return '09:00'; // Default to 9:00 AM
    
    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  };

  const parseDeliveryOnTimeFile = async (workbook) => {
    try {
      console.log('📊 Parsing Delivery On-Time file...');
      
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const sheetData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      
      console.log('📊 Sheet has', sheetData.length, 'rows');
      
      // Find header row and required columns
      let headerRowIdx = -1;
      let destinationColIdx = -1;
      let departureColIdx = -1;  // Column M
      let arrivalColIdx = -1;    // Column N
      
      for (let i = 0; i < Math.min(10, sheetData.length); i++) {
        const row = sheetData[i] || [];
        
        for (let j = 0; j < row.length; j++) {
          const cell = row[j]?.toString() || '';
          
          // Look for destination column (Column E)
          if (cell.includes('Dest. Location Name') || cell.includes('Destination')) {
            destinationColIdx = j;
          }
          
          // Look for departure column (Column M)
          if (cell.includes('Orig. Date') || cell.includes('Departure')) {
            departureColIdx = j;
          }
          
          // Look for arrival column (Column N)
          if (cell.includes('Dest. Date (Arrival)') || cell === 'Dest. Date (Arrival)') {
            arrivalColIdx = j;
            headerRowIdx = i;
          }
        }
        
        if (headerRowIdx !== -1 && destinationColIdx !== -1 && departureColIdx !== -1 && arrivalColIdx !== -1) {
          console.log(`✅ Headers found at row ${headerRowIdx + 1}`);
          console.log(`  Destination column: index ${destinationColIdx}`);
          console.log(`  Departure column: index ${departureColIdx}`);
          console.log(`  Arrival column: index ${arrivalColIdx}`);
          break;
        }
      }
      
      if (headerRowIdx === -1 || destinationColIdx === -1 || departureColIdx === -1 || arrivalColIdx === -1) {
        throw new Error('Could not find required columns in delivery file (need Destination, Departure, and Arrival columns)');
      }
      
      // Load both dispatch times and target arrival times from API config
      let dispatchTimes = {};
      let targetArrivalTimes = {};
      
      try {
        const dispatchConfig = await api.getConfig('dispatchTimes');
        dispatchTimes = dispatchConfig || {};
      } catch (error) {
        console.warn('Could not load dispatch times from API, using defaults:', error);
        dispatchTimes = {};
      }
      
      try {
        const arrivalConfig = await api.getConfig('targetArrivalTimes');
        targetArrivalTimes = arrivalConfig || {};
      } catch (error) {
        console.warn('Could not load target arrival times from API, using defaults:', error);
        targetArrivalTimes = {};
      }
      
      console.log('📅 Loaded dispatch times:', dispatchTimes);
      console.log('📅 Loaded target arrival times:', targetArrivalTimes);
      
      // Parse each delivery record
      const deliveries = [];
      
      // KPI 1: Outbound Departure tracking
      let departureOnTimeCount = 0;
      let departureLateCount = 0;
      
      // KPI 2: On Time Delivery (Arrival) tracking
      let arrivalOnTimeCount = 0;
      let arrivalMissCount = 0;
      
      let unknownCount = 0;
      
      for (let i = headerRowIdx + 1; i < sheetData.length; i++) {
        const row = sheetData[i] || [];
        
        const destination = row[destinationColIdx];
        const departureTime = row[departureColIdx];  // Column M
        const arrivalTime = row[arrivalColIdx];      // Column N
        
        if (!destination || !departureTime || !arrivalTime) continue;
        
        // Map destination to platform code
        const platformCode = findPlatformCode(destination);
        
        if (!platformCode) {
          console.warn(`⚠️ Could not map destination: "${destination}"`);
          unknownCount++;
          continue;
        }
        
        // Get target times for this platform
        const targetDispatchTime = dispatchTimes[platformCode];
        const targetArrival = targetArrivalTimes[platformCode];
        
        if (!targetDispatchTime || !targetArrival) {
          console.warn(`⚠️ No target times configured for platform: ${platformCode} (dispatch: ${targetDispatchTime}, arrival: ${targetArrival})`);
          unknownCount++;
          continue;
        }
        
        // Manual parsing for format: "10/06/2025 11:38AM" or "10/06/2025 5:00PM"
        const parseDateTime = (dateTimeStr) => {
          try {
            // Split into date and time parts
            const parts = dateTimeStr.split(' ');
            if (parts.length !== 2) return null;
            
            const datePart = parts[0]; // "10/06/2025"
            const timePart = parts[1]; // "11:38AM"
            
            // Parse date
            const [month, day, year] = datePart.split('/').map(Number);
            
            // Parse time
            const timeMatch = timePart.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
            if (!timeMatch) return null;
            
            let hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            const period = timeMatch[3].toUpperCase();
            
            // Convert to 24-hour format
            if (period === 'PM' && hours !== 12) {
              hours += 12;
            } else if (period === 'AM' && hours === 12) {
              hours = 0;
            }
            
            return { hours, minutes };
          } catch (error) {
            return null;
          }
        };

        // Parse departure and arrival times
        const departureStr = departureTime.toString().trim();
        const arrivalStr = arrivalTime.toString().trim();
        
        const parsedDeparture = parseDateTime(departureStr);
        const parsedArrival = parseDateTime(arrivalStr);

        if (!parsedDeparture || !parsedArrival) {
          console.warn(`⚠️ Could not parse times - Departure: "${departureStr}", Arrival: "${arrivalStr}"`);
          unknownCount++;
          continue;
        }

        // Convert to minutes from midnight for easier calculation
        const actualDepartureMinutes = parsedDeparture.hours * 60 + parsedDeparture.minutes;
        const actualArrivalMinutes = parsedArrival.hours * 60 + parsedArrival.minutes;
        
        // Parse target times (format: "09:00" from time input, or "9:30 AM" from display)
        const parseTargetTime = (targetTime) => {
          if (!targetTime || !targetTime.includes(':')) return null;
          
          const targetParts = targetTime.split(':');
          let targetHours = parseInt(targetParts[0]);
          let targetMinutes = parseInt(targetParts[1]);
          
          // If target is in 12-hour format (like "12:30 PM"), handle it
          if (targetTime.includes('PM') && targetHours !== 12) {
            targetHours += 12;
          } else if (targetTime.includes('AM') && targetHours === 12) {
            targetHours = 0;
          }
          
          return targetHours * 60 + targetMinutes;
        };

        const targetDispatchMinutes = parseTargetTime(targetDispatchTime);
        const targetArrivalMinutes = parseTargetTime(targetArrival);

        if (targetDispatchMinutes === null || targetArrivalMinutes === null) {
          console.warn(`⚠️ Invalid target time formats - Dispatch: "${targetDispatchTime}", Arrival: "${targetArrival}"`);
          unknownCount++;
          continue;
        }
        
        // ========================================
        // KPI 1: OUTBOUND DEPARTURE CALCULATION
        // ========================================
        // Simple comparison: Departure Time <= Dispatch Time
        const isDepartureOnTime = actualDepartureMinutes <= targetDispatchMinutes;
        
        if (isDepartureOnTime) {
          departureOnTimeCount++;
        } else {
          departureLateCount++;
        }
        
        // ========================================
        // KPI 2: ON TIME DELIVERY (ARRIVAL) CALCULATION
        // ========================================
        // Arrival Window: 45 minutes BEFORE to 30 minutes AFTER target
        const earliestAcceptable = targetArrivalMinutes - 45;  // 45 min before
        const latestAcceptable = targetArrivalMinutes + 30;    // 30 min after
        
        const isArrivalOnTime = actualArrivalMinutes >= earliestAcceptable && actualArrivalMinutes <= latestAcceptable;
        
        if (isArrivalOnTime) {
          arrivalOnTimeCount++;
        } else {
          arrivalMissCount++;
        }
        
        // Format times for display
        const departureTimeStr = `${parsedDeparture.hours.toString().padStart(2, '0')}:${parsedDeparture.minutes.toString().padStart(2, '0')}`;
        const arrivalTimeStr = `${parsedArrival.hours.toString().padStart(2, '0')}:${parsedArrival.minutes.toString().padStart(2, '0')}`;
        
        deliveries.push({
          platform: platformCode,
          destination: destination,
          actualDeparture: departureTimeStr,
          targetDispatch: targetDispatchTime,
          isDepartureOnTime: isDepartureOnTime,
          actualArrival: arrivalTimeStr,
          targetArrival: targetArrival,
          isArrivalOnTime: isArrivalOnTime,
          arrivalWindow: `${Math.floor(earliestAcceptable/60).toString().padStart(2,'0')}:${(earliestAcceptable%60).toString().padStart(2,'0')} to ${Math.floor(latestAcceptable/60).toString().padStart(2,'0')}:${(latestAcceptable%60).toString().padStart(2,'0')}`
        });
        
        if (deliveries.length <= 10) {
          console.log(`  Delivery ${deliveries.length}: ${platformCode}`);
          console.log(`    Departure: ${departureTimeStr} vs ${targetDispatchTime} = ${isDepartureOnTime ? '✅ ON TIME' : '❌ LATE'}`);
          console.log(`    Arrival: ${arrivalTimeStr} vs ${targetArrival} (window: ${Math.floor(earliestAcceptable/60)}:${(earliestAcceptable%60).toString().padStart(2,'0')}-${Math.floor(latestAcceptable/60)}:${(latestAcceptable%60).toString().padStart(2,'0')}) = ${isArrivalOnTime ? '✅ ON TIME' : '❌ MISS'}`);
        }
      }
      
      // Calculate percentages for both KPIs
      const totalDepartures = departureOnTimeCount + departureLateCount;
      const totalArrivals = arrivalOnTimeCount + arrivalMissCount;
      
      const departureOnTimePercent = totalDepartures > 0 ? (departureOnTimeCount / totalDepartures) * 100 : 0;
      const arrivalOnTimePercent = totalArrivals > 0 ? (arrivalOnTimeCount / totalArrivals) * 100 : 0;
      
      console.log('📊 Delivery Performance Summary:');
      console.log(`  Total deliveries evaluated: ${totalDepartures} (departures) / ${totalArrivals} (arrivals)`);
      console.log('  ========================================');
      console.log('  KPI 1: OUTBOUND DEPARTURE');
      console.log(`    On-time departures: ${departureOnTimeCount} (${departureOnTimePercent.toFixed(1)}%)`);
      console.log(`    Late departures: ${departureLateCount}`);
      console.log('  ========================================');
      console.log('  KPI 2: ON TIME DELIVERY (ARRIVAL)');
      console.log(`    On-time arrivals: ${arrivalOnTimeCount} (${arrivalOnTimePercent.toFixed(1)}%)`);
      console.log(`    Missed arrivals: ${arrivalMissCount}`);
      console.log('  ========================================');
      console.log(`  Unknown/skipped: ${unknownCount}`);
      
      // Store historical data (could be saved to API in future)
      const deliveryData = {
        uploadDate: new Date().toISOString(),
        // KPI 1: Outbound Departure
        departureOnTimePercent: departureOnTimePercent,
        departureOnTimeCount: departureOnTimeCount,
        departureLateCount: departureLateCount,
        totalDepartures: totalDepartures,
        // KPI 2: On Time Delivery (Arrival)
        arrivalOnTimePercent: arrivalOnTimePercent,
        arrivalOnTimeCount: arrivalOnTimeCount,
        arrivalMissCount: arrivalMissCount,
        totalArrivals: totalArrivals,
        // Details
        deliveries: deliveries
      };
      
      // Note: Historical data could be stored via API in future iterations
      console.log('📊 Delivery historical data:', deliveryData);
      
      return {
        // Return both KPI values
        outboundDeparture: departureOnTimePercent.toFixed(1),
        onTimeDelivery: arrivalOnTimePercent.toFixed(1),
        // Additional data for display
        departureOnTimeCount: departureOnTimeCount,
        departureLateCount: departureLateCount,
        arrivalOnTimeCount: arrivalOnTimeCount,
        arrivalMissCount: arrivalMissCount,
        totalDeliveries: Math.max(totalDepartures, totalArrivals)
      };
      
    } catch (error) {
      console.error('Error parsing delivery file:', error);
      return { 
        outboundDeparture: 'Error', 
        onTimeDelivery: 'Error', 
        totalDeliveries: 0 
      };
    }
  };

  const createKPICard = (name, uom, value = 'N/A', color = '#757575') => (
    <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
          {name}
        </Typography>
        <Typography variant="h4" sx={{ color, fontWeight: 600, my: 1 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
          {uom}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Page Header */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            KPI Tracking Dashboard
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Key Performance Indicators for Warehouses, Platforms, and Financial Performance
          </Typography>
        </Grid>

        {isLoadingKpis ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading KPI data...
              </Typography>
            </Box>
          </Grid>
        ) : (
          <>
        {/* PHASE 1 TEST: Inbound Section with New Layout */}
        <Grid item xs={12}>
          <Box sx={{
            bgcolor: '#e8f5e9',
            borderLeft: '6px solid #2e7d32',
            borderRadius: 1,
            p: 2,
            mt: 5,
            mb: 3
          }}>
            <Typography variant="h5" sx={{ 
              color: '#2e7d32', 
              fontWeight: 700,
              fontSize: '1.5rem'
            }}>
              Warehouses KPIs
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32', fontWeight: 700 }}>
              Inbound (IB)
            </Typography>
            <Grid container spacing={3}>
              {/* Vendor Stated Fill Rate */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('vendorFillRate', 'Vendor Stated Fill Rate', KPI_DRILL_DOWN_CONFIG.vendorFillRate)}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Vendor Stated Fill Rate
                </Typography>
                <InfoTooltip text={KPI_CALCULATIONS.vendorFillRate} />
              </Box>
              <Typography variant="h4" sx={{ 
                my: 1, 
                color: '#1976d2',
                fontWeight: 600
              }}>
                {kpiValues.vendorFillRate || 'N/A'}%
              </Typography>
              {!kpiValues.vendorFillRate && (
                <Typography variant="caption" color="textSecondary">
                  Upload Fill Rate report
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Vendor Converted (Units Sold) */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '3px solid #1976d2',
              bgcolor: '#f5f9ff',
              boxShadow: '0 2px 8px rgba(25,118,210,0.15)',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('vendorsConvertedSales', 'Vendor Converted (Units Sold)', KPI_DRILL_DOWN_CONFIG.vendorsConvertedSales)}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Vendor Converted (Units Sold)
                </Typography>
                <InfoTooltip text={KPI_CALCULATIONS.vendorsConvertedSales} />
              </Box>
              <Typography variant="h4" sx={{ 
                my: 1, 
                color: '#1976d2',
                fontWeight: 600
              }}>
                {kpiValues.vendorsConvertedSales || 'N/A'}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendors Converted (Count) */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('vendorsConvertedCount', 'Vendors Converted (Count)', KPI_DRILL_DOWN_CONFIG.vendorsConvertedCount)}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Vendors Converted (Count)
                </Typography>
                <InfoTooltip text={KPI_CALCULATIONS.vendorsConvertedCount} />
              </Box>
              <Typography variant="h4" sx={{ 
                my: 1, 
                color: '#1976d2',
                fontWeight: 600
              }}>
                {kpiValues.vendorsConvertedCount || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Dock-to-stock */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('dockToStock', 'Dock-to-Stock', KPI_DRILL_DOWN_CONFIG.dockToStock)}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Dock-to-stock
                </Typography>
                <InfoTooltip text={KPI_CALCULATIONS.dockToStock} />
              </Box>
              <Typography variant="h4" sx={{ 
                my: 1, 
                color: getKpiColor(kpiValues.dockToStock, kpiGoals.dockToStock, true),
                fontWeight: 600
              }}>
                {kpiValues.dockToStock ? Math.round(kpiValues.dockToStock) : 'N/A'}
              </Typography>
              {kpiGoals.dockToStock && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Goal: {kpiGoals.dockToStock} hrs
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Outbound (OB) Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32', fontWeight: 700 }}>
              Outbound (OB)
            </Typography>
            <Grid container spacing={3}>
              {/* LPH Picking */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '3px solid #1976d2',
                  bgcolor: '#f5f9ff',
                  boxShadow: '0 2px 8px rgba(25,118,210,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('lphPicking', 'LPH (Picking)', KPI_DRILL_DOWN_CONFIG.lphPicking)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      LPH (Picking)
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.lphPicking} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.lphPicking, kpiGoals.lphPicking, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.lphPicking ? Math.round(kpiValues.lphPicking) : 'N/A'}
                  </Typography>
                  {kpiGoals.lphPicking && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.lphPicking} lines/hr
                    </Typography>
                  )}
                </CardContent>
              </Card>
        </Grid>

        {/* Warehouse Fill Rate */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '3px solid #1976d2',
              bgcolor: '#f5f9ff',
              boxShadow: '0 2px 8px rgba(25,118,210,0.15)',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('pickAccuracy', 'Warehouse Fill Rate', KPI_DRILL_DOWN_CONFIG.pickAccuracy)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Warehouse Fill Rate
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.pickAccuracy} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.pickAccuracy, kpiGoals.pickAccuracy, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.pickAccuracy || 'N/A'}%
                  </Typography>
                  {kpiGoals.pickAccuracy && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.pickAccuracy}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
        </Grid>

        {/* Outbound Departure */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('outboundServiceLevel', 'Outbound Departure', KPI_DRILL_DOWN_CONFIG.outboundServiceLevel)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Outbound Departure
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.outboundServiceLevel} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.outboundServiceLevel, kpiGoals.outboundServiceLevel, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.outboundServiceLevel || 'N/A'}%
                  </Typography>
                  {kpiGoals.outboundServiceLevel && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.outboundServiceLevel}%
                    </Typography>
                  )}
            </CardContent>
          </Card>
        </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Inventory Management (IM) Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32', fontWeight: 700 }}>
              Inventory Management (IM)
            </Typography>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('inventoryAccuracy', 'Inventory Accuracy', KPI_DRILL_DOWN_CONFIG.inventoryAccuracy)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Inventory Accuracy
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.inventoryAccuracy} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.inventoryAccuracy, kpiGoals.inventoryAccuracy, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.inventoryAccuracy || 'N/A'}%
                  </Typography>
                  {kpiGoals.inventoryAccuracy && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.inventoryAccuracy}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('warehouseInStock', 'SKU In-stock (Warehouse)', KPI_DRILL_DOWN_CONFIG.warehouseInStock)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      SKU In-stock (Warehouse)
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.skuInStock} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.warehouseInStock, kpiGoals.skuInStock, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.warehouseInStock || 'N/A'}%
                  </Typography>
                  {kpiGoals.skuInStock && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.skuInStock}%
                    </Typography>
                  )}
                  {kpiValues.totalSkuCount && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      {kpiValues.totalSkuCount.toLocaleString()} SKUs tracked
                    </Typography>
                  )}
                  {!kpiValues.warehouseInStock && (
                    <Typography variant="caption" color="textSecondary">
                      Upload Zero On Hands report
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

        {/* Warehouse Inventory */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleKpiClick('warehouseInventory', 'Warehouse Inventory', KPI_DRILL_DOWN_CONFIG.warehouseInventory)}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Warehouse Inventory
                </Typography>
                <InfoTooltip text={KPI_CALCULATIONS.warehouseInventory} />
              </Box>
              <Typography variant="h4" sx={{ 
                my: 1, 
                color: '#1976d2',
                fontWeight: 600
              }}>
                {kpiValues.warehouseInventory || 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                SKUs in stock
              </Typography>
              {kpiValues.warehouseInventoryCost && (
                <Typography variant="body2" sx={{ 
                  mt: 1,
                  color: '#1976d2',
                  fontWeight: 500
                }}>
                  ${Math.round(kpiValues.warehouseInventoryCost).toLocaleString()} total value
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Building (Bldg) Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32', fontWeight: 700 }}>
              Building (Bldg)
            </Typography>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('storageUtilization', 'Storage Utilization', KPI_DRILL_DOWN_CONFIG.storageUtilization)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Storage Utilization
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.storageUtilization} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.storageUtilization, kpiGoals.storageUtilization, true),
                    fontWeight: 600
                  }}>
                    {kpiValues.storageUtilization ? Math.round(kpiValues.storageUtilization) : 'N/A'}%
                  </Typography>
                  {kpiGoals.storageUtilization && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: below {Math.round(kpiGoals.storageUtilization)}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Platforms KPIs Section */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <Box sx={{
            bgcolor: '#fff3e0',
            borderLeft: '6px solid #ed6c02',
            borderRadius: 1,
            p: 2,
            mt: 5,
            mb: 3,
            width: '100%'
          }}>
            <Typography variant="h5" sx={{ 
              color: '#ed6c02', 
              fontWeight: 700,
              fontSize: '1.5rem'
            }}>
              Platforms KPIs
            </Typography>
          </Box>
        </Grid>

        {/* Platforms Inbound Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#ed6c02', fontWeight: 700 }}>
              Inbound (IB)
            </Typography>
            <Grid container spacing={3}>
              {/* On Time Delivery */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '3px solid #1976d2',
                  bgcolor: '#f5f9ff',
                  boxShadow: '0 2px 8px rgba(25,118,210,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('onTimeDelivery', 'On Time Delivery', KPI_DRILL_DOWN_CONFIG.onTimeDelivery)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      On Time Delivery
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.onTimeDelivery} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.onTimeDelivery, kpiGoals.onTimeDelivery, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.onTimeDelivery || 'N/A'}%
                  </Typography>
                  {kpiGoals.onTimeDelivery && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.onTimeDelivery}%
                    </Typography>
                  )}
                  {!kpiValues.onTimeDelivery && (
                    <Typography variant="caption" color="textSecondary">
                      Upload delivery report
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('receivingUph', 'Receiving UPH in Store', KPI_DRILL_DOWN_CONFIG.receivingUph)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Receiving UPH in Store
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.receivingUph} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: '#757575',
                    fontWeight: 600
                  }}>
                    N/A
                  </Typography>
                  {kpiGoals.receivingUph && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.receivingUph} units/hr
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('complete4hrs', 'Complete in 4 hrs post Truck', KPI_DRILL_DOWN_CONFIG.complete4hrs)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Complete in 4 hrs post Truck
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.complete4hrs} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.complete4hrs, kpiGoals.complete4hrs, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.complete4hrs ? Math.round(kpiValues.complete4hrs) : 'N/A'}%
                  </Typography>
                  {kpiGoals.complete4hrs && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.complete4hrs}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
        </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Inventory Control (IC) Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#ed6c02', fontWeight: 700 }}>
              Inventory Control (IC)
            </Typography>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('inventoryAccuracyPlatform', 'Inventory Accuracy (Platform)', KPI_DRILL_DOWN_CONFIG.inventoryAccuracyPlatform)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Inventory Accuracy (Platform)
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.inventoryAccuracyPlatform} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.inventoryAccuracyPlatform, kpiGoals.inventoryAccuracyPlatform, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.inventoryAccuracyPlatform || 'N/A'}%
                  </Typography>
                  {kpiGoals.inventoryAccuracyPlatform && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.inventoryAccuracyPlatform}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '3px solid #1976d2',
                  bgcolor: '#f5f9ff',
                  boxShadow: '0 2px 8px rgba(25,118,210,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('skuInStockPlatform', 'SKU In-stock (Platform)', KPI_DRILL_DOWN_CONFIG.skuInStockPlatform)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      SKU In-stock (Platform)
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.skuInStockPlatform} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.skuInStockPlatform, kpiGoals.skuInStockPlatform, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.skuInStockPlatform || 'N/A'}%
                  </Typography>
                  {kpiGoals.skuInStockPlatform && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.skuInStockPlatform}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Labor Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#ed6c02', fontWeight: 700 }}>
              Labor
            </Typography>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('scheduleScore', 'BY Schedule Score (Replenishment)', KPI_DRILL_DOWN_CONFIG.scheduleScore)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      BY Schedule Score (Replenishment)
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.scheduleScore} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.scheduleScore, kpiGoals.scheduleScore, false),
                    fontWeight: 600
                  }}>
                    {kpiValues.scheduleScore || 'N/A'}%
                  </Typography>
                  {kpiGoals.scheduleScore && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: {kpiGoals.scheduleScore}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Cost Subsection */}
        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#ed6c02', fontWeight: 700 }}>
              Cost
            </Typography>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('deliveryCost', 'Delivery Cost per Delivery', KPI_DRILL_DOWN_CONFIG.deliveryCost)}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Delivery Cost per Delivery
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.deliveryCost} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: getKpiColor(kpiValues.deliveryCost, kpiGoals.deliveryCost, true),
                    fontWeight: 600
                  }}>
                    {kpiValues.deliveryCost ? `$${Math.round(kpiValues.deliveryCost)}` : 'N/A'}
                  </Typography>
                  {kpiGoals.deliveryCost && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Goal: ${kpiGoals.deliveryCost}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Total Financial Performance Section */}
        <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%' }}>
          <Box sx={{
            bgcolor: '#e3f2fd',
            borderLeft: '6px solid #1976d2',
            borderRadius: 1,
            p: 2,
            mt: 5,
            mb: 3,
            width: '100%'
          }}>
            <Typography variant="h5" sx={{ 
              color: '#1976d2', 
              fontWeight: 700,
              fontSize: '1.5rem'
            }}>
              Total Financial Performance
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fafafa',
            mt: 4
          }}>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('costCompareBase', 'Cost Compare to Base', KPI_DRILL_DOWN_CONFIG.costCompareBase)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Cost per Unit
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.costCompareBase} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: '#1976d2',
                    fontWeight: 600
                  }}>
                    {kpiValues.costCompareBase ? `$${kpiValues.costCompareBase}` : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('varianceBudget', 'Variance to Budget', KPI_DRILL_DOWN_CONFIG.varianceBudget)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      Variance to Budget / E
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.varianceBudget} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: '#1976d2',
                    fontWeight: 600
                  }}>
                    {kpiValues.varianceBudget ? `${kpiValues.varianceBudget}%` : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleKpiClick('percentTotalSales', '% of Total Sales', KPI_DRILL_DOWN_CONFIG.percentTotalSales)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                      % of Total Sales
                    </Typography>
                    <InfoTooltip text={KPI_CALCULATIONS.percentTotalSales} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    my: 1, 
                    color: '#1976d2',
                    fontWeight: 600
                  }}>
                    {kpiValues.percentTotalSales || 'N/A'}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            </Grid>
          </Box>
        </Grid>
        </>)}

        {/* Report Upload Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mt: 4, border: '2px dashed #ccc' }}>
            <Typography variant="h6" gutterBottom>
              Upload Reports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload one or more Excel files to automatically update KPIs. You can select multiple files at once.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<span>📁</span>}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Choose File(s)'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  multiple
                  onChange={handleMultiFileUpload}
                />
              </Button>
            </Box>
            
            {/* Progress indicators for each file */}
            {uploadProgress.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {uploadProgress.map((file, idx) => (
                  <Box key={idx} sx={{ mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {file.status === 'pending' && <span>⏳</span>}
                      {file.status === 'processing' && <span>🔄</span>}
                      {file.status === 'success' && <span>✅</span>}
                      {file.status === 'error' && <span>❌</span>}
                      
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{file.name}</strong>
                      </Typography>
                      
                      {file.reportType && (
                        <Chip 
                          label={file.reportType} 
                          size="small" 
                          color="success"
                        />
                      )}
                    </Box>
                    
                    {file.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        Error: {file.error}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            
            {uploadStatus && (
              <Alert severity={uploadStatus.type} sx={{ mt: 2 }}>
                {uploadStatus.message}
              </Alert>
            )}
            
            {/* Show KPIs updated summary */}
            {!isProcessing && uploadProgress.length > 0 && uploadProgress.some(f => f.status === 'success') && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>KPIs Updated:</strong>
                </Typography>
                {kpiValues.vendorFillRate && (
                  <Typography variant="body2">
                    • Vendor Stated Fill Rate: {kpiValues.vendorFillRate}% (Week {kpiValues.currentWeek})
                  </Typography>
                )}
                {kpiValues.warehouseInStock && (
                  <Typography variant="body2">
                    • Warehouse In-Stock: {kpiValues.warehouseInStock}% ({kpiValues.totalSkuCount} SKUs)
                  </Typography>
                )}
                {kpiValues.onTimeDelivery && (
                  <Typography variant="body2">
                    • On-Time Delivery: {kpiValues.onTimeDelivery}% ({kpiValues.totalDeliveries} deliveries)
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

      </Grid>

      {/* Drill-Down Modal */}
      <KpiDrillDownModal
        open={drillDownModal.open}
        onClose={handleCloseDrillDown}
        kpiKey={drillDownModal.kpiKey}
        kpiLabel={drillDownModal.kpiLabel}
        drillDownType={drillDownModal.drillDownType}
        data={drillDownModal.data}
      />

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>
    </Container>
  );
};

export default KPITracking;